import { useEffect, useState } from 'react';
import { cardsApi } from './cardsApi';
import type { Card } from '../../../shared/types';

/**
 * Small module-level cache resolving a card NAME -> its primary image filename.
 * Decks store card names; this lets us render thumbnails without a bulk fetch.
 * Primed from search results (free), and lazily fetches anything still missing.
 *
 * Map value: string = image filename, null = known to have no image,
 *            undefined (absent) = not loaded yet.
 */
const imageByName = new Map<string, string | null>();
const inflight = new Map<string, Promise<void>>();

export function primeImage(name: string, image: string | null): void {
  if (!imageByName.has(name)) imageByName.set(name, image ?? null);
}

export function ensureImage(name: string): Promise<void> {
  if (imageByName.has(name)) return Promise.resolve();
  let p = inflight.get(name);
  if (!p) {
    p = cardsApi
      .get(name)
      .then(c => { imageByName.set(name, c.primaryImage ?? null); })
      .catch(() => { imageByName.set(name, null); })
      .finally(() => inflight.delete(name));
    inflight.set(name, p);
  }
  return p;
}

// Full-card cache (used for client-side deck synergy aggregation).
const cardByName = new Map<string, Card>();
const cardInflight = new Map<string, Promise<Card | null>>();

export function getFullCard(name: string): Promise<Card | null> {
  const cached = cardByName.get(name);
  if (cached) return Promise.resolve(cached);
  let p = cardInflight.get(name);
  if (!p) {
    p = cardsApi
      .get(name)
      .then(c => {
        cardByName.set(name, c);
        primeImage(c.name, c.primaryImage);
        primeMeta(c.name, c.category, c.types);
        return c;
      })
      .catch(() => null)
      .finally(() => cardInflight.delete(name));
    cardInflight.set(name, p);
  }
  return p;
}

// Lightweight meta (category + types) for deck-zone legality checks.
export interface CardMeta { category: string; types: string[] | null; }
const metaByName = new Map<string, CardMeta>();

export function primeMeta(name: string, category: string, types: string[] | null): void {
  if (!metaByName.has(name)) metaByName.set(name, { category, types });
}
export function getMeta(name: string): CardMeta | undefined {
  return metaByName.get(name);
}
export async function ensureMeta(name: string): Promise<CardMeta | undefined> {
  const cached = metaByName.get(name);
  if (cached) return cached;
  await getFullCard(name); // primes meta
  return metaByName.get(name);
}

export interface CardStats { atk: number | null; def: number | null; isMonster: boolean; }

/** ATK/DEF (and monster-ness) for a card, fetched+cached on demand. Null until
 *  loaded. Non-monsters report isMonster=false (callers show 0/0). */
export function useCardStats(name: string | null | undefined): CardStats | null {
  const [, force] = useState(0);
  useEffect(() => {
    if (name && !cardByName.has(name)) getFullCard(name).then(() => force(x => x + 1));
  }, [name]);
  if (!name) return null;
  const c = cardByName.get(name);
  if (!c) return null;
  return { atk: c.atk, def: c.def, isMonster: c.category.toLowerCase().includes('monster') };
}

/** Returns the cached image filename (or null), triggering a fetch if needed. */
export function useCardImage(name: string | null | undefined): string | null {
  const [, force] = useState(0);
  useEffect(() => {
    if (name && !imageByName.has(name)) {
      ensureImage(name).then(() => force(x => x + 1));
    }
  }, [name]);
  return name ? imageByName.get(name) ?? null : null;
}
